"use client";

import * as React from "react";
import { Brain, ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../primitives/collapsible";

type ReasoningContextValue = {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
};

const ReasoningContext = React.createContext<ReasoningContextValue | null>(null);

const AUTO_CLOSE_DELAY_MS = 1000;

const useReasoning = (): ReasoningContextValue => {
  const context = React.useContext(ReasoningContext);
  if (!context) {
    throw new Error("Reasoning components must be used within Reasoning");
  }
  return context;
};

type ReasoningProps = React.ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};

const Reasoning = React.forwardRef<React.ElementRef<typeof Collapsible>, ReasoningProps>(
  (
    { className, children, isStreaming = false, open, defaultOpen, onOpenChange, duration: durationProp, ...props },
    ref,
  ) => {
    const isControlled = open !== undefined;
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? isStreaming);
    const [duration, setDuration] = React.useState<number | undefined>(durationProp);
    const startedAtRef = React.useRef<number | null>(isStreaming ? Date.now() : null);
    const hasEverStreamedRef = React.useRef(isStreaming);
    const hasAutoClosedRef = React.useRef(false);

    const isOpen = isControlled ? open : internalOpen;

    const setIsOpen = React.useCallback(
      (nextOpen: boolean) => {
        if (!isControlled) {
          setInternalOpen(nextOpen);
        }
        onOpenChange?.(nextOpen);
      },
      [isControlled, onOpenChange],
    );

    React.useEffect(() => {
      if (durationProp !== undefined) {
        setDuration(durationProp);
      }
    }, [durationProp]);

    React.useEffect(() => {
      if (isStreaming) {
        hasEverStreamedRef.current = true;
        if (startedAtRef.current === null) {
          startedAtRef.current = Date.now();
        }
        if (!isOpen && defaultOpen !== false) {
          setIsOpen(true);
        }
        return;
      }

      if (startedAtRef.current !== null) {
        setDuration(Math.max(1, Math.ceil((Date.now() - startedAtRef.current) / 1000)));
        startedAtRef.current = null;
      }
    }, [defaultOpen, isOpen, isStreaming, setIsOpen]);

    React.useEffect(() => {
      if (!hasEverStreamedRef.current || isStreaming || !isOpen || hasAutoClosedRef.current) return;

      const timer = window.setTimeout(() => {
        setIsOpen(false);
        hasAutoClosedRef.current = true;
      }, AUTO_CLOSE_DELAY_MS);

      return () => window.clearTimeout(timer);
    }, [isOpen, isStreaming, setIsOpen]);

    const contextValue = React.useMemo(
      () => ({ duration, isOpen: Boolean(isOpen), isStreaming, setIsOpen }),
      [duration, isOpen, isStreaming, setIsOpen],
    );

    return (
      <ReasoningContext.Provider value={contextValue}>
        <Collapsible
          ref={ref}
          open={Boolean(isOpen)}
          onOpenChange={setIsOpen}
          className={cn("mb-3", className)}
          {...props}
        >
          {children}
        </Collapsible>
      </ReasoningContext.Provider>
    );
  },
);
Reasoning.displayName = "Reasoning";

type ReasoningTriggerProps = React.ComponentProps<typeof CollapsibleTrigger> & {
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => React.ReactNode;
};

const defaultGetThinkingMessage = (isStreaming: boolean, duration?: number): React.ReactNode => {
  if (isStreaming) {
    return <span className="animate-pulse">Rozumuje...</span>;
  }
  if (duration === undefined) {
    return <span>Rozumowanie</span>;
  }
  return <span>Rozumowanie ({duration} s)</span>;
};

const ReasoningTrigger = React.forwardRef<React.ElementRef<typeof CollapsibleTrigger>, ReasoningTriggerProps>(
  ({ className, children, getThinkingMessage = defaultGetThinkingMessage, ...props }, ref) => {
    const { duration, isOpen, isStreaming } = useReasoning();

    return (
      <CollapsibleTrigger
        ref={ref}
        className={cn(
          "flex w-full items-center gap-2 text-left text-xs font-medium text-gray-500 transition-colors hover:text-gray-900",
          className,
        )}
        {...props}
      >
        {children ?? (
          <>
            <Brain className="h-4 w-4" />
            {getThinkingMessage(isStreaming, duration)}
            <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen ? "rotate-180" : "rotate-0")} />
          </>
        )}
      </CollapsibleTrigger>
    );
  },
);
ReasoningTrigger.displayName = "ReasoningTrigger";

type ReasoningContentProps = React.ComponentProps<typeof CollapsibleContent> & {
  children: string;
};

const ReasoningContent = React.forwardRef<React.ElementRef<typeof CollapsibleContent>, ReasoningContentProps>(
  ({ className, children, ...props }, ref) => (
    <CollapsibleContent
      ref={ref}
      className={cn(
        "mt-2 whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-600",
        className,
      )}
      {...props}
    >
      {children}
    </CollapsibleContent>
  ),
);
ReasoningContent.displayName = "ReasoningContent";

export { Reasoning, ReasoningContent, ReasoningTrigger, useReasoning };
